import { useMemo, useState, type ReactNode } from 'react';
import './DataTable.css';

type SortValue = string | number | boolean | Date | null | undefined;
type SortDirection = 'asc' | 'desc';

export interface DataTableFilterOption {
  value: string;
  label: string;
}

export interface DataTableColumn<T> {
  id: string;
  header: string;
  render: (row: T) => ReactNode;
  value?: (row: T) => SortValue;
  sortable?: boolean;
  filter?: {
    type?: 'text' | 'select';
    placeholder?: string;
    options?: DataTableFilterOption[];
  };
  className?: string;
}

interface DataTableProps<T> {
  rows: T[];
  columns: DataTableColumn<T>[];
  getRowKey: (row: T) => string;
  emptyMessage?: string;
  initialSort?: { columnId: string; direction: SortDirection };
  caption?: string;
}

function comparable(value: SortValue): string | number {
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  return value == null ? '' : String(value).toLocaleLowerCase();
}

export function DataTable<T>({
  rows,
  columns,
  getRowKey,
  emptyMessage = 'No results found.',
  initialSort,
  caption,
}: DataTableProps<T>) {
  const [sort, setSort] = useState(initialSort ?? null as { columnId: string; direction: SortDirection } | null);
  const [filters, setFilters] = useState<Record<string, string>>({});

  const visibleRows = useMemo(() => {
    const filtered = rows.filter((row) => columns.every((column) => {
      const term = filters[column.id]?.trim().toLocaleLowerCase();
      if (!term || !column.filter) return true;
      return String(column.value?.(row) ?? '').toLocaleLowerCase().includes(term);
    }));

    if (!sort) return filtered;
    const column = columns.find((candidate) => candidate.id === sort.columnId);
    if (!column?.value) return filtered;

    return [...filtered].sort((left, right) => {
      const a = comparable(column.value?.(left));
      const b = comparable(column.value?.(right));
      const result = typeof a === 'number' && typeof b === 'number'
        ? a - b
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      return sort.direction === 'asc' ? result : -result;
    });
  }, [columns, filters, rows, sort]);

  const toggleSort = (columnId: string) => {
    setSort((current) => {
      if (!current || current.columnId !== columnId) return { columnId, direction: 'asc' };
      if (current.direction === 'asc') return { columnId, direction: 'desc' };
      return null;
    });
  };

  const hasFilters = columns.some((column) => column.filter);

  return (
    <div className="data-table-container">
      <table className="data-table">
        {caption && <caption>{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => {
              const canSort = Boolean(column.sortable && column.value);
              const activeSort = sort?.columnId === column.id ? sort.direction : null;
              return (
                <th key={column.id} className={`${canSort ? 'data-table__sortable' : ''} ${column.className ?? ''}`}>
                  {canSort ? (
                    <button type="button" onClick={() => toggleSort(column.id)} aria-label={`Sort by ${column.header}`}>
                      {column.header}
                      <span aria-hidden="true">{activeSort === 'asc' ? '▲' : activeSort === 'desc' ? '▼' : '↕'}</span>
                    </button>
                  ) : column.header}
                </th>
              );
            })}
          </tr>
          {hasFilters && (
            <tr className="data-table__filters">
              {columns.map((column) => (
                <td key={column.id}>
                  {column.filter?.type === 'select' ? (
                    <select
                      aria-label={`Filter ${column.header}`}
                      value={filters[column.id] ?? ''}
                      onChange={(event) => setFilters((current) => ({ ...current, [column.id]: event.target.value }))}
                    >
                      <option value="">{column.filter.placeholder ?? `All ${column.header}`}</option>
                      {column.filter.options?.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  ) : column.filter ? (
                    <input
                      type="search"
                      aria-label={`Filter ${column.header}`}
                      placeholder={column.filter.placeholder ?? `Filter ${column.header}…`}
                      value={filters[column.id] ?? ''}
                      onChange={(event) => setFilters((current) => ({ ...current, [column.id]: event.target.value }))}
                    />
                  ) : null}
                </td>
              ))}
            </tr>
          )}
        </thead>
        <tbody>
          {visibleRows.length > 0 ? visibleRows.map((row) => (
            <tr key={getRowKey(row)}>
              {columns.map((column) => <td key={column.id} className={column.className}>{column.render(row)}</td>)}
            </tr>
          )) : (
            <tr><td className="data-table__empty" colSpan={columns.length}>{emptyMessage}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default DataTable;
